// Shared week-view time-grid geometry for every persona calendar (supervisor,
// therapist, inclusion-head). Before this, each grid dropped a session into a
// fixed-height hour cell regardless of how long it ran, so a 30-minute probe and
// a 3-hour session looked identical and a session starting at :30 either snapped
// to the hour or (in one calendar) failed to render at all.
//
// This module is the single source of truth for turning a session's start time
// and duration into a pixel rectangle inside the day column — Outlook/Teams
// style — plus side-by-side packing for sessions whose times overlap. It is pure
// (no React, no DOM) so the three calendars stay identical and the maths can be
// reasoned about in one place.

// Default visible window is 7am–7pm (labels 7…18, the 18:00 row covering 6–7pm).
// Each calendar passes its own window because the personas differ — the
// inclusion-head grid runs 8am–6pm — so start/end hour are parameters, not
// constants, and these are only the fallbacks. A session outside the window is
// clamped to it (never dropped) — see clamping in computeDayColumnLayout.
export const GRID_START_HOUR = 7;
export const GRID_END_HOUR = 19; // exclusive end — 12 one-hour rows (7:00 … 18:00)

// Below this a stacked card cannot show two lines of text; used only to decide
// whether a rendered card is "compact" (smaller fonts), never to change geometry.
export const COMPACT_CARD_HEIGHT = 34;

/**
 * 'HH:MM' → minutes since midnight. Returns null for anything unparseable so a
 * malformed row is skipped, never rendered at a garbage position.
 */
const parseHHMM = (value) => {
  if (typeof value !== 'string') return null;
  const m = value.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
};

/**
 * Resolve a session's [startMin, endMin] in minutes-since-midnight.
 *
 * Start comes from `startTime` ('HH:MM'). End is derived, in order of trust:
 *   1. `endTime` ('HH:MM') when it is after the start,
 *   2. else start + `duration` (minutes),
 *   3. else a 60-minute default so the card is at least visible.
 * A non-positive or missing span always falls back to 60 minutes rather than
 * collapsing to a zero-height sliver.
 */
const resolveSpan = (session) => {
  const startMin = parseHHMM(session.startTime);
  if (startMin == null) return null;

  let endMin = parseHHMM(session.endTime);
  if (endMin == null || endMin <= startMin) {
    const dur = Number(session.duration);
    endMin = startMin + (Number.isFinite(dur) && dur > 0 ? dur : 60);
  }
  return { startMin, endMin };
};

/**
 * Pack a single day's sessions into positioned rectangles for absolute layout.
 *
 * @param {Array} sessions - sessions for ONE day (any order)
 * @param {object} [opts]
 * @param {number} [opts.hourHeight=60] - pixels per hour row (must match the grid)
 * @param {number} [opts.gapPx=2] - horizontal gap between side-by-side columns
 * @param {number} [opts.startHour=GRID_START_HOUR] - first hour label in the grid
 * @param {number} [opts.endHour=GRID_END_HOUR] - exclusive end hour of the grid
 * @returns {Array} one entry per renderable session, in start-time order:
 *   { session, topPx, heightPx, leftPct, widthPct, compact }
 *   - topPx/heightPx: vertical placement (clamped to the visible window)
 *   - leftPct/widthPct: horizontal share when times overlap (0–100)
 *   - compact: true when the card is too short for two comfortable text lines
 *
 * Overlap handling is the classic calendar column-packing: sessions that
 * transitively overlap in time form a cluster, each is assigned the first free
 * column, and the cluster's column count sets every member's width. Non-
 * overlapping sessions each get the full width. This mirrors what Outlook/Teams
 * do and replaces the old "shove them into one flex row inside the hour cell".
 */
export const computeDayColumnLayout = (
  sessions,
  { hourHeight = 60, gapPx = 2, startHour = GRID_START_HOUR, endHour = GRID_END_HOUR } = {}
) => {
  const pxPerMin = hourHeight / 60;
  const gridStart = startHour * 60;
  const gridEnd = endHour * 60;

  // 1. Resolve spans, drop unrenderable rows, clamp to the visible window.
  const items = [];
  (sessions || []).forEach((session) => {
    const span = resolveSpan(session);
    if (!span) return;
    // Skip sessions entirely outside the window; clamp partial overhangs so a
    // long session shows up to the window edge instead of overflowing the grid.
    if (span.endMin <= gridStart || span.startMin >= gridEnd) return;
    const startMin = Math.max(span.startMin, gridStart);
    const endMin = Math.min(span.endMin, gridEnd);
    items.push({ session, startMin, endMin });
  });

  // 2. Order by start, then longer-first, so column assignment is stable.
  items.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  // 3. Column-pack into clusters of transitively-overlapping sessions.
  const results = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  const flushCluster = () => {
    if (cluster.length === 0) return;
    // Greedy column assignment within the cluster.
    const columnEnds = []; // last endMin per column
    cluster.forEach((it) => {
      let col = columnEnds.findIndex((end) => end <= it.startMin);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(it.endMin);
      } else {
        columnEnds[col] = it.endMin;
      }
      it.col = col;
    });
    const colCount = columnEnds.length;
    cluster.forEach((it) => {
      const heightPx = Math.max((it.endMin - it.startMin) * pxPerMin, 18);
      // Convert the gap (px) into a percentage of the day column so columns keep
      // a hair of space between them without knowing the column's pixel width.
      const widthPct = 100 / colCount;
      results.push({
        session: it.session,
        topPx: (it.startMin - gridStart) * pxPerMin,
        heightPx,
        leftPct: it.col * widthPct,
        widthPct,
        gapPx: colCount > 1 ? gapPx : 0,
        compact: heightPx < COMPACT_CARD_HEIGHT
      });
    });
    cluster = [];
    clusterEnd = -Infinity;
  };

  items.forEach((it) => {
    if (it.startMin >= clusterEnd && cluster.length > 0) {
      flushCluster();
    }
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.endMin);
  });
  flushCluster();

  return results;
};

// Total pixel height of the day body for a given window and hour-row height —
// used to size the relative container that events are positioned within. Must
// equal (number of hour rows) × hourHeight so events line up with the gridlines.
export const gridBodyHeight = (hourHeight = 60, startHour = GRID_START_HOUR, endHour = GRID_END_HOUR) => (endHour - startHour) * hourHeight;
